import { useTranslation } from '@govard/i18n-core/react';
import { i18n } from '../../../i18n/index.js';
// Единая форма контакта — создание и правка одним компонентом, как у компании,
// задачи и встречи. До этого создание жило самодельной разметкой на странице
// списка, а редактирования не было вовсе: PATCH /contacts/:id существовал, но
// из интерфейса менялась только привязка к компании.
import { useEffect, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Form, Input, Modal, Select } from 'antd';

import type { ContactItem, CreateContactInput } from '../api/contacts.client.js';
import { TagChip } from '../../../components/entity-detail/TagChip.js';
import { getEnumLabel, matchesTagSearch, SYSTEM_TAGS } from '../../../shared/format/enum-labels.js';
import { useCompanies } from '../../tasks/hooks/useTaskLookups.js';
import {
  DuplicateContactError,
  useCreateContactMutation,
  useUpdateContactMutation,
} from '../hooks/useContactMutations.js';

interface ContactFormValues {
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
  companyId?: string | undefined;
  tags: string[];
}

interface ContactFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Передан — режим редактирования; иначе создание. */
  contact?: ContactItem | null;
  /** Форма открыта с карточки компании: поле скрыто, значение проставлено жёстко. */
  lockedCompanyId?: string;
}

export function ContactFormModal({
  open,
  onClose,
  contact,
  lockedCompanyId,
}: ContactFormModalProps): ReactElement {
  const { t } = useTranslation();
  const [form] = Form.useForm<ContactFormValues>();
  const isEdit = Boolean(contact);

  const companiesQuery = useCompanies();
  const createMut = useCreateContactMutation();
  const updateMut = useUpdateContactMutation();
  const pending = createMut.isPending || updateMut.isPending;
  const error = (createMut.error ?? updateMut.error) as Error | null;
  // Дубликат — не ошибка формы, а подсказка «такой контакт уже есть».
  const duplicate = error instanceof DuplicateContactError ? error.suggestion : null;

  useEffect(() => {
    if (!open) return;
    createMut.reset();
    updateMut.reset();
    form.setFieldsValue({
      firstName: contact?.firstName ?? '',
      lastName: contact?.lastName ?? '',
      email: contact?.email ?? '',
      phone: contact?.phone ?? '',
      companyId: contact?.companyId ?? lockedCompanyId ?? undefined,
      tags: contact?.tags ?? [],
    });
  }, [open, contact, lockedCompanyId, form]);

  const handleSubmit = (values: ContactFormValues): void => {
    const companyId = lockedCompanyId ?? values.companyId ?? null;
    if (isEdit && contact) {
      // В правке пустое поле — это «очистить», поэтому шлём null, а не пропускаем ключ.
      updateMut.mutate(
        {
          id: contact.id,
          input: {
            firstName: values.firstName.trim(),
            lastName: values.lastName?.trim() ?? '',
            email: values.email?.trim() ?? '',
            phone: values.phone?.trim() ?? '',
            companyId,
            tags: values.tags,
            expectedUpdatedAt: contact.updatedAt,
          },
        },
        { onSuccess: () => onClose() },
      );
      return;
    }

    const input: CreateContactInput = {
      firstName: values.firstName.trim(),
      ...(values.lastName?.trim() ? { lastName: values.lastName.trim() } : {}),
      ...(values.email?.trim() ? { email: values.email.trim() } : {}),
      ...(values.phone?.trim() ? { phone: values.phone.trim() } : {}),
      ...(companyId ? { companyId } : {}),
      ...(values.tags.length ? { tags: values.tags } : {}),
    };
    createMut.mutate(input, { onSuccess: () => onClose() });
  };

  return (
    <Modal
      title={
        isEdit
          ? i18n.t(
              'crm.features.features.contacts.components.contactformmodal.редактировать_контакт',
            )
          : t('crm.contacts.create_title')
      }
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={
        isEdit
          ? i18n.t('crm.features.features.contacts.components.contactformmodal.сохранить')
          : t('crm.contacts.create_submit')
      }
      cancelText={i18n.t('crm.features.features.contacts.components.contactformmodal.отмена')}
      confirmLoading={pending}
      width={520}
      destroyOnHidden
    >
      {duplicate ? (
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          title={t('crm.contacts.duplicate_banner')}
          description={
            <Link
              to={`/contacts/${duplicate.id}`}
              aria-label={t('crm.contacts.open_contact')}
              onClick={onClose}
            >
              {t('crm.contacts.open_contact')}: {duplicate.fullName}
            </Link>
          }
        />
      ) : error ? (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          title={
            isEdit
              ? i18n.t(
                  'crm.features.features.contacts.components.contactformmodal.не_удалось_сохранить_контакт',
                )
              : i18n.t(
                  'crm.features.features.contacts.components.contactformmodal.не_удалось_создать_контакт',
                )
          }
          description={error.message}
        />
      ) : null}

      <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={pending}>
        <div className="flex gap-3">
          <Form.Item
            name="firstName"
            label={t('crm.contacts.form.first_name')}
            className="flex-1"
            rules={[
              {
                required: true,
                whitespace: true,
                message: i18n.t(
                  'crm.features.features.contacts.components.contactformmodal.введите_имя',
                ),
              },
            ]}
          >
            <Input
              placeholder={i18n.t(
                'crm.features.features.contacts.components.contactformmodal.иван',
              )}
              maxLength={100}
              autoFocus
            />
          </Form.Item>
          <Form.Item name="lastName" label={t('crm.contacts.form.last_name')} className="flex-1">
            <Input
              placeholder={i18n.t(
                'crm.features.features.contacts.components.contactformmodal.петров',
              )}
              maxLength={100}
            />
          </Form.Item>
        </div>

        <div className="flex gap-3">
          <Form.Item
            name="email"
            label={t('crm.contacts.form.email')}
            className="flex-1"
            rules={[
              {
                type: 'email',
                message: i18n.t(
                  'crm.features.features.contacts.components.contactformmodal.похоже_это_не_email',
                ),
              },
            ]}
          >
            <Input placeholder="ivan@example.com" maxLength={200} />
          </Form.Item>
          <Form.Item name="phone" label={t('crm.contacts.form.phone')} className="flex-1">
            <Input placeholder="+7 999 000-00-00" maxLength={50} />
          </Form.Item>
        </div>

        {lockedCompanyId ? null : (
          <Form.Item
            name="companyId"
            label={i18n.t('crm.features.features.contacts.components.contactformmodal.компания')}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={i18n.t(
                'crm.features.features.contacts.components.contactformmodal.без_компании',
              )}
              loading={companiesQuery.isLoading}
              options={(companiesQuery.data ?? []).map((company) => ({
                value: company.id,
                label: company.name,
              }))}
            />
          </Form.Item>
        )}
        <Form.Item name="tags" label={t('crm.entity_detail.tags')}>
          <Select<string[]>
            classNames={{ popup: { root: 'entity-tag-options' } }}
            menuItemSelectedIcon={null}
            mode="tags"
            allowClear
            aria-label={t('crm.entity_detail.tags')}
            placeholder={t('crm.entity_detail.all_tags')}
            options={SYSTEM_TAGS.map((value) => ({ value, label: getEnumLabel('tag', value, t) }))}
            filterOption={(search, option) =>
              matchesTagSearch(String(option?.value ?? ''), search, t)
            }
            optionRender={(option) => <TagChip value={String(option.value)} />}
            tagRender={({ value, closable, onClose }) => (
              <TagChip
                value={String(value)}
                closable={closable}
                onClose={onClose}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              />
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
